import { Fade, Tooltip, TooltipProps, styled, tooltipClasses } from '@mui/material';

interface styledTooltipProps extends TooltipProps {
  placement?: 'bottom' | 'top' | 'left' | 'right';
  maxWidth?: number;
  tooltipBgColor?: string;
  variant?: 'default' | 'message';
}

const CUSTOM_TOOLTIP_PROPS = new Set(['maxWidth', 'tooltipBgColor', 'variant']);

const StyledTooltip = styled(
  ({ className, placement, ...props }: styledTooltipProps) => (
    <Tooltip
      {...props}
      arrow
      classes={{ popper: className }}
      placement={placement || 'top'}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 200 }}
      slotProps={{
        popper: {
          /** Above portaled UI (e.g. filter dropdown at z-index 99999). */
          sx: { zIndex: 100100 },
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        },
      }}
    />
  ),
  {
    shouldForwardProp: (prop) => !CUSTOM_TOOLTIP_PROPS.has(prop as string),
  },
)(({ theme, maxWidth, tooltipBgColor, variant = 'default' }) => {
  const isMessage = variant === 'message';
  const bgColor = isMessage ? '#ffffff' : (tooltipBgColor ?? theme.palette.common.black);

  return {
    [`& .${tooltipClasses.arrow}`]: {
      color: bgColor,
      ...(isMessage && {
        '&::before': {
          border: '1px solid #E4E4E7',
        },
      }),
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: bgColor,
      color: isMessage ? '#344054' : theme.palette.common.white,
      maxWidth,
      maxHeight: 200,
      overflowY: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      ...(isMessage && {
        border: '1px solid #E4E4E7',
        borderRadius: '10px',
        boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)',
        fontSize: '12px',
        lineHeight: 1.5,
        padding: '10px 12px',
      }),
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  };
});

export default StyledTooltip;
